import { type ReactNode, useState } from 'react';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { isValidShifts } from '~/commonComponents/InputJob/Utils';
import { isUnlikelyShift } from '~/components/Request/multiday/utils';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ArrivalTimeField } from './ArrivalTimeField';
import { DepartureTimeField } from './DepartureTimeField';

const DEFAULT_HELPER_TEXT =
  'Set arrival 15-30 minutes before the first patient to allow time to prepare.';

interface ITimeFieldsWrapperProps {
  arrivalValue: string;
  onArrivalChange: (value: string) => void;
  departureValue: string;
  onDepartureChange: (value: string) => void;
  labelRight?: ReactNode;
  hasArrivalError?: boolean;
  hasDepartureError?: boolean;
  /**
   * Helper text rendered below the time inputs.
   * - `undefined` (default): shows the default shift-request copy
   *   ("Set arrival 15-30 minutes…").
   * - `null`: hides the helper text entirely.
   * - any other ReactNode: overrides the default with the provided content.
   */
  helperText?: ReactNode | null;
}

export const TimeFieldsWrapper = ({
  arrivalValue,
  onArrivalChange,
  departureValue,
  onDepartureChange,
  labelRight,
  hasArrivalError,
  hasDepartureError,
  helperText,
}: ITimeFieldsWrapperProps) => {
  const resolvedHelperText = helperText === undefined ? DEFAULT_HELPER_TEXT : helperText;
  const [showInvalidTimePopup, setShowInvalidTimePopup] = useState(false);
  const [showUnlikelyShiftPopup, setShowUnlikelyShiftPopup] = useState(false);
  const [pendingTimeChange, setPendingTimeChange] = useState<{
    field: 'arrival' | 'departure';
    value: string;
  } | null>(null);

  const handleTimeChange = (
    field: 'arrival' | 'departure',
    newValue: string,
    applyChange: (value: string) => void,
    otherValue: string,
  ) => {
    const arrival = field === 'arrival' ? newValue : otherValue;
    const departure = field === 'departure' ? newValue : otherValue;

    if (!arrival || !departure) {
      applyChange(newValue);
      return;
    }

    if (!isValidShifts(arrival, departure)) {
      setShowInvalidTimePopup(true);
      return;
    }

    if (isUnlikelyShift(arrival, departure)) {
      setPendingTimeChange({ field, value: newValue });
      setShowUnlikelyShiftPopup(true);
      return;
    }

    applyChange(newValue);
  };

  const handleArrivalChange = (newArrival: string) => {
    handleTimeChange('arrival', newArrival, onArrivalChange, departureValue);
  };

  const handleDepartureChange = (newDeparture: string) => {
    handleTimeChange('departure', newDeparture, onDepartureChange, arrivalValue);
  };

  const handleConfirmUnlikelyShift = () => {
    if (pendingTimeChange) {
      const applyChange =
        pendingTimeChange.field === 'arrival' ? onArrivalChange : onDepartureChange;
      applyChange(pendingTimeChange.value);
    }
    setPendingTimeChange(null);
    setShowUnlikelyShiftPopup(false);
  };

  const handleDismissUnlikelyShift = () => {
    setPendingTimeChange(null);
    setShowUnlikelyShiftPopup(false);
  };

  const displayArrival =
    pendingTimeChange?.field === 'arrival' ? pendingTimeChange.value : arrivalValue;
  const displayDeparture =
    pendingTimeChange?.field === 'departure' ? pendingTimeChange.value : departureValue;

  return (
    <>
      <Column gap={3}>
        <Row style={{ gap: 30 }}>
          <Column style={{ flex: 1 }}>
            <ArrivalTimeField
              value={arrivalValue}
              onChange={handleArrivalChange}
              labelRight={labelRight}
              hasError={hasArrivalError}
            />
          </Column>
          <Column style={{ flex: 1 }}>
            <DepartureTimeField
              value={departureValue}
              onChange={handleDepartureChange}
              labelRight={labelRight}
              hasError={hasDepartureError}
            />
          </Column>
        </Row>
        {resolvedHelperText != null &&
          (typeof resolvedHelperText === 'string' ? (
            <Typography as="span" size="body_large" color={colors.neutral[500]}>
              {resolvedHelperText}
            </Typography>
          ) : (
            resolvedHelperText
          ))}
      </Column>

      {showInvalidTimePopup && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.primary[500]} />
            </div>
          )}
          title="Sorry!"
          description="Arrival Time cannot be after Departure Time"
          rightButtonText="Close"
          rightButtonAction={() => setShowInvalidTimePopup(false)}
          closePopup={() => setShowInvalidTimePopup(false)}
          dataTestId="invalid-time-popup"
        />
      )}

      {showUnlikelyShiftPopup && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.primary[500]} />
            </div>
          )}
          title="Please Review Shift Time"
          description={
            <>
              <Typography as="span" color={colors.neutral[500]} textAlign="center">
                The hours you&apos;ve entered appear to be outside the usual range. If they&apos;re
                accurate, click confirm. Otherwise, you can go back to make edits.
              </Typography>
              <Column style={{ marginTop: 18, gap: 10 }}>
                <Row justify="between" style={{ marginLeft: 65, marginRight: 65 }}>
                  <Typography as="span" color={colors.neutral[500]} bold>
                    Arrival Time:
                  </Typography>
                  <Typography as="span" color={colors.neutral[500]}>{displayArrival}</Typography>
                </Row>
                <Row justify="between" style={{ marginLeft: 65, marginRight: 65 }}>
                  <Typography as="span" color={colors.neutral[500]} bold>
                    Departure Time:
                  </Typography>
                  <Typography as="span" color={colors.neutral[500]}>{displayDeparture}</Typography>
                </Row>
              </Column>
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={handleDismissUnlikelyShift}
          rightButtonText="Confirm"
          rightButtonAction={handleConfirmUnlikelyShift}
          closePopup={handleDismissUnlikelyShift}
          dataTestId="unlikely-shift-popup"
        />
      )}
    </>
  );
};
